import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Calendar, Users, CheckCircle2, XCircle, Clock, AlertTriangle,
  Download, Filter, Search, ChevronLeft, ChevronRight, Fingerprint,
  CreditCard, Hand, Sparkles, RefreshCw, Layers, Building2,
  CalendarDays, Check, X, Shield, ArrowRight, UserCheck, Eye
} from 'lucide-react';
import axiosInstance from '../networking/axiosconfig';
import Navbar from './Navbar';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function AttendanceRegister() {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState(now.toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('matrix'); // 'matrix' | 'daily'

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');

  // Cell inspection modal
  const [inspectModal, setInspectModal] = useState({
    open: false,
    employee: null,
    dateStr: '',
    attendance: null,
    loading: false,
    dayDetails: null,
    saving: false,
    statusOverride: '',
    note: ''
  });

  const daysInMonth = useMemo(() => {
    return new Date(selectedYear, selectedMonthIndex + 1, 0).getDate();
  }, [selectedYear, selectedMonthIndex]);

  const monthName = MONTH_NAMES[selectedMonthIndex];

  // Fetch holidays
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const res = await axiosInstance.get('/api/holiday/get/holiday');
        if (res.data?.status && Array.isArray(res.data?.data)) {
          setHolidays(res.data.data);
        }
      } catch (e) {
        // Fallback gracefully
      }
    };
    fetchHolidays();
  }, []);

  // Fetch attendance list for full month
  const fetchMonthAttendance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const startDay = '01';
      const endDay = String(daysInMonth).padStart(2, '0');
      const monthNum = String(selectedMonthIndex + 1).padStart(2, '0');
      const startdate = `${selectedYear}-${monthNum}-${startDay}`;
      const enddate = `${selectedYear}-${monthNum}-${endDay}`;

      const res = await axiosInstance.get('/api/attendance/getattendanceListByDate', {
        params: { startdate, enddate }
      });

      if (res.data?.status && Array.isArray(res.data?.data)) {
        setEmployees(res.data.data);
      } else {
        setEmployees([]);
      }
    } catch (err) {
      console.error('Failed to load attendance:', err);
      setError('Attendance records could not be loaded. Please ensure you are logged in.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonthIndex, daysInMonth]);

  useEffect(() => {
    fetchMonthAttendance();
  }, [fetchMonthAttendance]);

  // Month navigation
  const handlePrevMonth = () => {
    if (selectedMonthIndex === 0) {
      setSelectedMonthIndex(11);
      setSelectedYear((y) => y - 1);
    } else {
      setSelectedMonthIndex((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonthIndex === 11) {
      setSelectedMonthIndex(0);
      setSelectedYear((y) => y + 1);
    } else {
      setSelectedMonthIndex((m) => m + 1);
    }
  };

  const handleToday = () => {
    const today = new Date();
    setSelectedYear(today.getFullYear());
    setSelectedMonthIndex(today.getMonth());
    setSelectedDate(today.toISOString().split('T')[0]);
  };

  // Helper for weekdays and holidays
  const getDayInfo = useCallback(
    (dayNumber) => {
      const d = new Date(selectedYear, selectedMonthIndex, dayNumber);
      const dayOfWeek = d.getDay();
      const isSunday = dayOfWeek === 0;
      const isSaturday = dayOfWeek === 6;
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = dayNames[dayOfWeek];

      const monthNum = String(selectedMonthIndex + 1).padStart(2, '0');
      const dayStr = String(dayNumber).padStart(2, '0');
      const fullDateStr = `${selectedYear}-${monthNum}-${dayStr}`;

      const holidayMatch = holidays.find((h) => {
        const hDate = (h.date || '').split('T')[0];
        return hDate === fullDateStr;
      });

      return {
        fullDateStr,
        dayName,
        isSunday,
        isSaturday,
        isHoliday: Boolean(holidayMatch),
        holidayName: holidayMatch?.holiday_name || ''
      };
    },
    [selectedYear, selectedMonthIndex, holidays]
  );

  // Departments list for filter
  const departments = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => {
      if (e.department_name) set.add(e.department_name);
    });
    return Array.from(set);
  }, [employees]);

  // Filtered employees
  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.toLowerCase();
      const email = (emp.email || '').toLowerCase();
      const query = searchQuery.toLowerCase().trim();
      const matchSearch = !query || fullName.includes(query) || email.includes(query);
      const matchDept =
        departmentFilter === 'all' || emp.department_name === departmentFilter;
      return matchSearch && matchDept;
    });
  }, [employees, searchQuery, departmentFilter]);

  // Resolve attendance status for a single cell
  const resolveDayStatus = (emp, dayNumber) => {
    const { fullDateStr, isSunday, isHoliday, holidayName } = getDayInfo(dayNumber);
    const attList = emp.attendance || [];
    const record = attList.find((a) => (a.date || '').split('T')[0] === fullDateStr);

    if (record) {
      const st = record.status;
      const late = record.late_status === 1 || record.late_status === 2;

      let code = 'P';
      let label = 'Present';
      let bg = 'bg-emerald-500 text-white';
      let ring = '';

      if (st === 1 || st === 5 || st === 8) {
        code = 'P';
        label = 'Present';
        bg = 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm';
      } else if (st === 2) {
        code = 'A';
        label = 'Absent';
        bg = 'bg-rose-500 text-white hover:bg-rose-600 shadow-sm';
      } else if (st === 3) {
        code = 'H';
        label = 'Holiday';
        bg = 'bg-cyan-500 text-white hover:bg-cyan-600';
      } else if (st === 4 || st === 6) {
        code = 'L';
        label = 'Leave';
        bg = 'bg-indigo-500 text-white hover:bg-indigo-600';
      } else if (st === 7) {
        code = 'WO';
        label = 'Week Off';
        bg = 'bg-slate-200 text-slate-700 hover:bg-slate-300';
      }

      if (late) {
        ring = 'ring-2 ring-amber-400 ring-offset-1';
      }

      return {
        hasRecord: true,
        record,
        code,
        label,
        bg,
        ring,
        isLate: late,
        checkIn: record.check_in_time,
        checkOut: record.check_out_time,
        workHours: record.total_working_hours || 0,
        manualMsg: record.manual_message
      };
    }

    if (isHoliday) {
      return {
        hasRecord: false,
        record: null,
        code: 'H',
        label: holidayName || 'Holiday',
        bg: 'bg-cyan-100 text-cyan-800 border border-cyan-200',
        ring: '',
        isLate: false
      };
    }

    if (isSunday) {
      return {
        hasRecord: false,
        record: null,
        code: 'WO',
        label: 'Sunday',
        bg: 'bg-slate-100 text-slate-500 border border-slate-200',
        ring: '',
        isLate: false
      };
    }

    // Default unmarked past vs future
    const cellDate = new Date(selectedYear, selectedMonthIndex, dayNumber);
    const todayZero = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (cellDate < todayZero) {
      return {
        hasRecord: false,
        record: null,
        code: '-',
        label: 'Not Marked',
        bg: 'bg-gray-100 text-gray-400',
        ring: '',
        isLate: false
      };
    }

    return {
      hasRecord: false,
      record: null,
      code: '',
      label: 'Upcoming',
      bg: 'bg-transparent text-gray-300',
      ring: '',
      isLate: false
    };
  };

  // KPI Calculations across entire team
  const kpiStats = useMemo(() => {
    let presentCount = 0;
    let absentCount = 0;
    let lateCount = 0;
    let leaveCount = 0;

    // Calculate for today's date if within selected month, else entire month totals
    const todayStr = now.toISOString().split('T')[0];

    employees.forEach((emp) => {
      (emp.attendance || []).forEach((att) => {
        const attDate = (att.date || '').split('T')[0];
        if (attDate === todayStr) {
          if (att.status === 1 || att.status === 5 || att.status === 8) presentCount++;
          if (att.status === 2) absentCount++;
          if (att.late_status === 1 || att.late_status === 2) lateCount++;
          if (att.status === 4 || att.status === 6) leaveCount++;
        }
      });
    });

    return {
      totalStaff: employees.length,
      presentToday: presentCount,
      absentToday: absentCount,
      lateToday: lateCount,
      leaveToday: leaveCount
    };
  }, [employees, now]);

  // Open inspection modal
  const openInspector = async (emp, dayNumber) => {
    const { fullDateStr } = getDayInfo(dayNumber);
    const cellStatus = resolveDayStatus(emp, dayNumber);

    setInspectModal({
      open: true,
      employee: emp,
      dateStr: fullDateStr,
      attendance: cellStatus.record,
      loading: true,
      dayDetails: null,
      saving: false,
      statusOverride: cellStatus.record ? String(cellStatus.record.status) : '1',
      note: cellStatus.record?.manual_message || ''
    });

    try {
      const res = await axiosInstance.get('/api/attendance/day-summary', {
        params: { userId: emp.user_id, date: fullDateStr }
      });
      if (res.data?.status) {
        setInspectModal((prev) => ({
          ...prev,
          loading: false,
          dayDetails: res.data.data
        }));
      } else {
        setInspectModal((prev) => ({ ...prev, loading: false }));
      }
    } catch (e) {
      setInspectModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Save manual attendance status override
  const handleSaveStatusOverride = async () => {
    if (!inspectModal.employee || !inspectModal.dateStr) return;
    try {
      setInspectModal((prev) => ({ ...prev, saving: true }));
      const payload = {
        userId: inspectModal.employee.user_id,
        date: inspectModal.dateStr,
        status: Number(inspectModal.statusOverride),
        message: inspectModal.note
      };
      await axiosInstance.post('/api/attendance/update-status', payload);

      setInspectModal((prev) => ({ ...prev, open: false, saving: false }));
      fetchMonthAttendance();
    } catch (e) {
      alert('Failed to update status: ' + (e.response?.data?.message || e.message));
      setInspectModal((prev) => ({ ...prev, saving: false }));
    }
  };

  // Export Matrix to CSV
  const exportToCSV = () => {
    const headers = ['Employee ID', 'Name', 'Department', 'Email'];
    for (let d = 1; d <= daysInMonth; d++) {
      headers.push(`Day ${d}`);
    }
    headers.push('Total Present', 'Total Absent', 'Total Late');

    const rows = [headers];

    filteredEmployees.forEach((emp) => {
      let pCount = 0;
      let aCount = 0;
      let lCount = 0;

      const row = [
        emp.user_id,
        `"${emp.first_name || ''} ${emp.last_name || ''}"`,
        `"${emp.department_name || '-'}"`,
        emp.email || '-'
      ];

      for (let d = 1; d <= daysInMonth; d++) {
        const cell = resolveDayStatus(emp, d);
        row.push(cell.code || '-');
        if (cell.code === 'P') pCount++;
        if (cell.code === 'A') aCount++;
        if (cell.isLate) lCount++;
      }

      row.push(pCount, aCount, lCount);
      rows.push(row);
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + rows.map((e) => e.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Attendance_Register_${monthName}_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatTimestamp = (ts) => {
    if (!ts || ts === 0) return '--:--';
    const date = new Date(ts * 1000);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-12">
      <Navbar />

      <div className="max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* Top Header & PagarBook Banner */}
        <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-sky-800 rounded-2xl p-6 sm:p-8 text-white shadow-xl mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20">
                <CalendarDays className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                Staff Attendance Register (हाज़िरी रजिस्टर)
              </h1>
            </div>
            <p className="text-blue-100 text-sm sm:text-base max-w-2xl">
              Commercial multi-machine attendance ledger. Real-time biometric sync with{' '}
              <span className="font-semibold text-white">T700 (RFID/Finger)</span> &{' '}
              <span className="font-semibold text-white">T800 (Face/Palm/RFID)</span>.
            </p>
          </div>

          {/* Month Navigator Controls */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-md p-2 rounded-2xl border border-white/20">
            <button
              onClick={handlePrevMonth}
              className="p-2.5 rounded-xl hover:bg-white/20 transition text-white active:scale-95"
              title="Previous Month"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="px-3 py-1 text-center min-w-[140px]">
              <span className="text-lg font-bold block leading-none">
                {monthName} {selectedYear}
              </span>
              <span className="text-xs text-blue-200 uppercase tracking-wider font-semibold">
                {daysInMonth} Days
              </span>
            </div>

            <button
              onClick={handleNextMonth}
              className="p-2.5 rounded-xl hover:bg-white/20 transition text-white active:scale-95"
              title="Next Month"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <button
              onClick={handleToday}
              className="ml-2 px-3.5 py-2 rounded-xl bg-white text-blue-700 font-semibold text-xs sm:text-sm hover:bg-blue-50 transition shadow-sm active:scale-95"
            >
              Today
            </button>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Total Staff</p>
              <p className="text-2xl font-bold text-slate-800">{kpiStats.totalStaff}</p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Present Today</p>
              <p className="text-2xl font-bold text-emerald-600">{kpiStats.presentToday}</p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center font-bold">
              <XCircle className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Absent Today</p>
              <p className="text-2xl font-bold text-rose-600">{kpiStats.absentToday}</p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Late Arrivals</p>
              <p className="text-2xl font-bold text-amber-600">{kpiStats.lateToday}</p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm flex items-center gap-3 col-span-2 sm:col-span-1">
            <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">On Leave</p>
              <p className="text-2xl font-bold text-indigo-600">{kpiStats.leaveToday}</p>
            </div>
          </div>
        </div>

        {/* View Switcher, Filter & Search Toolbar */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm mb-6 flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Tabs */}
          <div className="flex items-center gap-2 bg-slate-100 p-1.5 rounded-xl w-full md:w-auto">
            <button
              onClick={() => setActiveTab('matrix')}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
                activeTab === 'matrix'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Layers className="w-4 h-4" />
              Monthly Matrix Grid
            </button>
            <button
              onClick={() => setActiveTab('daily')}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
                activeTab === 'daily'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Clock className="w-4 h-4" />
              Daily Live Punches
            </button>
          </div>

          {/* Search, Dept Filter & Export */}
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
            <div className="relative flex-1 sm:w-64">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search staff name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition"
              />
            </div>

            {departments.length > 0 && (
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer"
              >
                <option value="all">All Departments</option>
                {departments.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm transition active:scale-95"
            >
              <Download className="w-4 h-4" />
              Export Register
            </button>

            <button
              onClick={fetchMonthAttendance}
              className="p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 transition"
              title="Refresh Attendance"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-blue-600' : ''}`} />
            </button>
          </div>
        </div>

        {/* Legend strip */}
        <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-600 mb-4 px-2">
          <span className="text-slate-400 font-medium">Legend:</span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold">P</span>
            Present
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-rose-500 text-white flex items-center justify-center text-[10px] font-bold">A</span>
            Absent
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold">HD</span>
            Half Day
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-indigo-500 text-white flex items-center justify-center text-[10px] font-bold">L</span>
            Leave
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-slate-200 text-slate-700 flex items-center justify-center text-[10px] font-bold">WO</span>
            Week Off
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-cyan-500 text-white flex items-center justify-center text-[10px] font-bold">H</span>
            Holiday
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded ring-2 ring-amber-400 bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold">P</span>
            Late In
          </span>
        </div>

        {/* TAB 1: Monthly Attendance Matrix Grid */}
        {activeTab === 'matrix' && (
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md overflow-hidden">
            {loading && employees.length === 0 ? (
              <div className="p-16 text-center">
                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-slate-600 font-medium">Loading attendance matrix...</p>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="p-16 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-semibold text-lg">No staff found</p>
                <p className="text-slate-400 text-sm">Try adjusting your search or department filter.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse select-none">
                  <thead>
                    <tr className="bg-slate-100/80 border-b border-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider">
                      {/* Sticky Employee Column */}
                      <th className="sticky left-0 z-20 bg-slate-100 px-4 py-3 min-w-[240px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.06)]">
                        Staff Name & Dept
                      </th>

                      {/* Day Columns */}
                      {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((dayNum) => {
                        const { dayName, isSunday, isHoliday } = getDayInfo(dayNum);
                        return (
                          <th
                            key={dayNum}
                            className={`px-1.5 py-2 text-center min-w-[38px] max-w-[42px] border-r border-slate-200/60 ${
                              isSunday
                                ? 'bg-red-50 text-red-700 font-bold'
                                : isHoliday
                                ? 'bg-cyan-50 text-cyan-800'
                                : 'text-slate-700'
                            }`}
                          >
                            <span className="block text-xs font-bold">{dayNum}</span>
                            <span className="block text-[10px] font-semibold text-slate-400">
                              {dayName}
                            </span>
                          </th>
                        );
                      })}

                      {/* Summary Columns */}
                      <th className="px-3 py-2 text-center bg-emerald-50 text-emerald-800 font-bold min-w-[50px]">
                        P
                      </th>
                      <th className="px-3 py-2 text-center bg-rose-50 text-rose-800 font-bold min-w-[50px]">
                        A
                      </th>
                      <th className="px-3 py-2 text-center bg-amber-50 text-amber-800 font-bold min-w-[50px]">
                        HD
                      </th>
                      <th className="px-3 py-2 text-center bg-indigo-50 text-indigo-800 font-bold min-w-[50px]">
                        L
                      </th>
                      <th className="px-3 py-2 text-center bg-slate-100 text-slate-700 font-bold min-w-[70px]">
                        Hours
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 text-sm">
                    {filteredEmployees.map((emp) => {
                      let totalP = 0;
                      let totalA = 0;
                      let totalHD = 0;
                      let totalL = 0;
                      let totalMinutes = 0;

                      return (
                        <tr key={emp.user_id} className="hover:bg-blue-50/40 transition">
                          {/* Sticky Employee Cell */}
                          <td className="sticky left-0 z-10 bg-white group-hover:bg-blue-50/40 px-4 py-3 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.06)]">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center overflow-hidden border border-slate-200 flex-shrink-0">
                                {emp.user_profile_img ? (
                                  <img
                                    src={emp.user_profile_img}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  `${(emp.first_name || 'U')[0]}${(emp.last_name || '')[0] || ''}`
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-900 truncate">
                                  {emp.first_name} {emp.last_name}
                                </p>
                                <p className="text-xs text-slate-400 truncate">
                                  {emp.department_name || 'General'}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Days Cells */}
                          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((dayNum) => {
                            const dayCell = resolveDayStatus(emp, dayNum);
                            const { isSunday } = getDayInfo(dayNum);

                            if (dayCell.code === 'P') totalP++;
                            if (dayCell.code === 'A') totalA++;
                            if (dayCell.code === 'HD') totalHD++;
                            if (dayCell.code === 'L') totalL++;
                            if (dayCell.workHours) totalMinutes += dayCell.workHours;

                            return (
                              <td
                                key={dayNum}
                                onClick={() => openInspector(emp, dayNum)}
                                className={`px-1 py-1.5 text-center border-r border-slate-100 cursor-pointer transition hover:scale-105 ${
                                  isSunday ? 'bg-red-50/30' : ''
                                }`}
                                title={`${emp.first_name}: Day ${dayNum} (${dayCell.label})`}
                              >
                                {dayCell.code ? (
                                  <div
                                    className={`w-7 h-7 mx-auto rounded-lg flex items-center justify-center font-bold text-xs transition-transform ${dayCell.bg} ${dayCell.ring}`}
                                  >
                                    {dayCell.code}
                                  </div>
                                ) : (
                                  <span className="text-slate-300">-</span>
                                )}
                              </td>
                            );
                          })}

                          {/* Row Counters */}
                          <td className="px-2 py-3 text-center font-bold text-emerald-600 bg-emerald-50/30">
                            {totalP}
                          </td>
                          <td className="px-2 py-3 text-center font-bold text-rose-600 bg-rose-50/30">
                            {totalA}
                          </td>
                          <td className="px-2 py-3 text-center font-bold text-amber-600 bg-amber-50/30">
                            {totalHD}
                          </td>
                          <td className="px-2 py-3 text-center font-bold text-indigo-600 bg-indigo-50/30">
                            {totalL}
                          </td>
                          <td className="px-2 py-3 text-center font-semibold text-slate-700 bg-slate-50/50 text-xs">
                            {Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: Daily Live Punches View */}
        {activeTab === 'daily' && (
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-6 mb-6 border-b border-slate-100 gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  Daily Biometric Punch Logs
                </h2>
                <p className="text-slate-500 text-sm">
                  First check-in, last check-out, duration, and biometric verification modes.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold text-slate-600">Select Date:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase text-slate-500 tracking-wider">
                    <th className="p-3">Staff Member</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Check-In</th>
                    <th className="p-3">Check-Out</th>
                    <th className="p-3">Total Hours</th>
                    <th className="p-3">Machine / Device</th>
                    <th className="p-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredEmployees.map((emp) => {
                    const att = (emp.attendance || []).find(
                      (a) => (a.date || '').split('T')[0] === selectedDate
                    );

                    const isPresent = att && (att.status === 1 || att.status === 5 || att.status === 8);
                    const isLate = att && (att.late_status === 1 || att.late_status === 2);

                    return (
                      <tr key={emp.user_id} className="hover:bg-slate-50/70 transition">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center overflow-hidden border border-slate-200">
                              {emp.user_profile_img ? (
                                <img src={emp.user_profile_img} alt="" className="w-full h-full object-cover" />
                              ) : (
                                `${(emp.first_name || 'U')[0]}${(emp.last_name || '')[0] || ''}`
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{emp.first_name} {emp.last_name}</p>
                              <p className="text-xs text-slate-400">{emp.department_name || 'General'}</p>
                            </div>
                          </div>
                        </td>

                        <td className="p-3">
                          {isPresent ? (
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                              isLate ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${isLate ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                              {isLate ? 'Present (Late)' : 'Present'}
                            </span>
                          ) : att?.status === 2 ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                              Absent
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs font-medium">Not punched</span>
                          )}
                        </td>

                        <td className="p-3 font-semibold text-slate-800">
                          {formatTimestamp(att?.check_in_time)}
                        </td>

                        <td className="p-3 font-semibold text-slate-800">
                          {formatTimestamp(att?.check_out_time)}
                        </td>

                        <td className="p-3 text-slate-700">
                          {att && att.total_working_hours > 0 ? (
                            <span className="font-medium text-slate-900">
                              {Math.floor(att.total_working_hours / 60)}h {att.total_working_hours % 60}m
                            </span>
                          ) : (
                            '--'
                          )}
                        </td>

                        <td className="p-3 text-slate-600">
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-100 text-xs font-medium text-slate-700">
                            <Fingerprint className="w-3.5 h-3.5 text-blue-600" />
                            {att?.device_name || 'T700 / T800'}
                          </span>
                        </td>

                        <td className="p-3">
                          <button
                            onClick={() => {
                              const dNum = parseInt(selectedDate.split('-')[2], 10);
                              openInspector(emp, dNum);
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Inspect
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* CELL INSPECTOR & ADJUSTMENT MODAL */}
      {inspectModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-700 to-indigo-700 p-6 text-white flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-blue-200 font-semibold">
                  Punch Inspection & Adjustment
                </p>
                <h3 className="text-xl font-bold">
                  {inspectModal.employee?.first_name} {inspectModal.employee?.last_name}
                </h3>
                <p className="text-xs text-blue-100 mt-0.5">
                  Date: {inspectModal.dateStr}
                </p>
              </div>
              <button
                onClick={() => setInspectModal((prev) => ({ ...prev, open: false }))}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
              {inspectModal.loading ? (
                <div className="py-12 text-center">
                  <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm text-slate-500 font-medium">Fetching biometric punch history...</p>
                </div>
              ) : (
                <>
                  {/* Punch Summary Banner */}
                  <div className="grid grid-cols-3 gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="text-center">
                      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">First In</p>
                      <p className="text-base font-bold text-slate-900">
                        {formatTimestamp(inspectModal.attendance?.check_in_time)}
                      </p>
                    </div>
                    <div className="text-center border-x border-slate-200">
                      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Last Out</p>
                      <p className="text-base font-bold text-slate-900">
                        {formatTimestamp(inspectModal.attendance?.check_out_time)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Working Hours</p>
                      <p className="text-base font-bold text-emerald-600">
                        {inspectModal.attendance?.total_working_hours
                          ? `${Math.floor(inspectModal.attendance.total_working_hours / 60)}h ${inspectModal.attendance.total_working_hours % 60}m`
                          : '0h 0m'}
                      </p>
                    </div>
                  </div>

                  {/* Biometric Punches List */}
                  <div>
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Fingerprint className="w-4 h-4 text-blue-600" />
                      Recorded Biometric Punches
                    </h4>

                    {inspectModal.dayDetails?.punches && inspectModal.dayDetails.punches.length > 0 ? (
                      <div className="space-y-2">
                        {inspectModal.dayDetails.punches.map((p, idx) => (
                          <div
                            key={p.id || idx}
                            className="flex items-center justify-between p-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition text-xs"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-2 py-0.5 rounded font-bold uppercase text-[10px] ${
                                  p.io_mode === 1
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-indigo-100 text-indigo-800'
                                }`}
                              >
                                {p.io_mode === 1 ? 'IN' : 'OUT'}
                              </span>
                              <span className="font-semibold text-slate-800">
                                {p.io_time ? p.io_time.split(' ')[1] : '--:--'}
                              </span>
                            </div>

                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold">
                                {p.verify_mode === 'CARD' && <CreditCard className="w-3 h-3" />}
                                {p.verify_mode === 'FP' && <Fingerprint className="w-3 h-3" />}
                                {p.verify_mode === 'FACE' && <Users className="w-3 h-3" />}
                                {p.verify_mode === 'PALM' && <Hand className="w-3 h-3" />}
                                {p.verify_mode || 'BIOMETRIC'}
                              </span>
                              <span className="text-slate-400 text-[11px]">
                                {p.device_name || p.dev_id || 'T700/T800'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 rounded-xl bg-slate-50 text-center border border-dashed border-slate-200 text-xs text-slate-400">
                        No raw punch timestamps recorded for this day.
                      </div>
                    )}
                  </div>

                  {/* Manual Status Override */}
                  <div className="pt-2 border-t border-slate-100">
                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                      Mark / Override Status
                    </h4>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {[
                        { val: '1', label: 'Present', color: 'hover:border-emerald-500' },
                        { val: '2', label: 'Absent', color: 'hover:border-rose-500' },
                        { val: '4', label: 'Leave', color: 'hover:border-indigo-500' },
                        { val: '3', label: 'Holiday', color: 'hover:border-cyan-500' }
                      ].map((item) => (
                        <button
                          key={item.val}
                          type="button"
                          onClick={() => setInspectModal((prev) => ({ ...prev, statusOverride: item.val }))}
                          className={`p-2.5 rounded-xl border text-xs font-bold text-center transition ${item.color} ${
                            inspectModal.statusOverride === item.val
                              ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                              : 'bg-slate-50 text-slate-700 border-slate-200'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">
                        Adjustment Note (Optional)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. On-site client visit / manual approval"
                        value={inspectModal.note}
                        onChange={(e) => setInspectModal((prev) => ({ ...prev, note: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setInspectModal((prev) => ({ ...prev, open: false }))}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-200 transition"
              >
                Close
              </button>
              <button
                type="button"
                disabled={inspectModal.saving || inspectModal.loading}
                onClick={handleSaveStatusOverride}
                className="px-5 py-2 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition active:scale-95 disabled:opacity-50"
              >
                {inspectModal.saving ? 'Saving...' : 'Save Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
